import OtpApprovalModal from "@/components/OtpApprovalModal";
import { useOpenTotpSetup } from "@/components/TotpSetupModal";
import { toaster } from "@/components/ui/toaster";
import { ROUTES } from "@/config/routes";
import { useFetchUser } from "@/hooks/useFetchUser";
import { usePermissions } from "@/hooks/usePermissions";
import {
  Box,
  Button,
  Flex,
  HStack,
  Heading,
  IconButton,
  Text,
} from "@chakra-ui/react";
import { ArrowLeft, CheckCircle2, Send, XCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useApprovePayoutBatch } from "../hooks/useApprovePayoutBatch";
import { useFetchPayoutBatch } from "../hooks/useFetchPayoutBatch";
import { useRejectPayoutBatch } from "../hooks/useRejectPayoutBatch";
import { useFetchBalance } from "../hooks/useFetchBalance";
import {
  isConfirmingPayment,
  useFetchBatchTopUps,
} from "../hooks/useFetchBatchTopUps";
import TopUpModal from "../components/TopUpModal";
import BatchFundingPanel from "./components/BatchFundingPanel";
import BatchInfo from "./components/BatchInfo";
import BatchItemsTable from "./components/BatchItemsTable";

export default function PayoutBatchDetailContainer(): JSX.Element {
  const { batchId } = useParams<{ batchId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const routeState = useRef(location.state as { fund?: boolean } | null);

  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [topUpModalOpen, setTopUpModalOpen] = useState(false);

  const { user } = useFetchUser();
  const openTotpSetup = useOpenTotpSetup();
  const { isPayoutApprover } = usePermissions();

  const { latest, approved } = useFetchBatchTopUps(batchId ?? null);
  const { batch, isLoading: isBatchesLoading } = useFetchPayoutBatch(
    batchId ?? null,
    isConfirmingPayment(latest) || !!approved
  );
  const { balance } = useFetchBalance();

  const awaitingFunds =
    batch?.status === "unfunded" || batch?.status === "funding";
  const shortfall = batch
    ? (batch.totalOutflows ??
        batch.totalAmount + batch.totalFees + (batch.gmfTotal ?? 0)) -
      (balance?.available ?? 0)
    : 0;

  useEffect(() => {
    if (location.state)
      navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    if (!batch || !user || !routeState.current) return;
    if (routeState.current.fund && batch.status === "unfunded")
      setTopUpModalOpen(isPayoutApprover);
    routeState.current = null;
  }, [batch, user, isPayoutApprover]);

  const { approveBatch, isApproving } = useApprovePayoutBatch(
    (approvedBatch) => {
      setOtpModalOpen(false);
      setOtpError(null);
      if (approvedBatch.status === "unfunded") {
        setTopUpModalOpen(true);
        return;
      }
      toaster.create({
        title: "Lote aprobado",
        description: "El lote de dispersión ha sido aprobado exitosamente.",
        type: "success",
        duration: 5000,
      });
    },
    (error) => {
      const message = error instanceof Error ? error.message : "";
      const code = (error as any)?.data?.code;
      if (
        otpModalOpen &&
        code === "FORBIDDEN" &&
        message.includes("TOTP locked")
      ) {
        setOtpError(
          "Clave Dinámica bloqueada temporalmente. Intenta más tarde."
        );
        return;
      }
      if (
        otpModalOpen &&
        code === "UNAUTHORIZED" &&
        message.includes("Invalid OTP")
      ) {
        setOtpError("Clave Dinámica inválida. Intenta de nuevo.");
        return;
      }
      if (message.includes("Insufficient balance")) {
        toaster.create({
          title: "Saldo insuficiente",
          description: "No tienes saldo disponible para aprobar este lote.",
          type: "error",
          duration: 5000,
        });
      } else {
        toaster.create({
          title: "Error",
          description:
            "Ocurrió un error al aprobar el lote. Por favor, intenta de nuevo más tarde.",
          type: "error",
          duration: 5000,
        });
      }
    }
  );
  const { rejectBatch, isRejecting } = useRejectPayoutBatch(
    () => {
      toaster.create({
        title: "Lote rechazado",
        description: "El lote de dispersión ha sido rechazado exitosamente.",
        type: "success",
        duration: 5000,
      });
    },
    () => {
      toaster.create({
        title: "Error",
        description:
          "Ocurrió un error al rechazar el lote. Por favor, intenta de nuevo más tarde.",
        type: "error",
        duration: 5000,
      });
    }
  );
  const handleApprove = async () => {
    if (!batchId) return;
    if (user?.showTotpSecretConfiguration) {
      openTotpSetup();
      return;
    }
    setOtpModalOpen(true);
    setOtpError(null);
  };

  const handleReject = async () => {
    if (batchId) {
      rejectBatch(batchId);
    }
  };

  const handleBack = () => {
    const historyState = window.history.state as { idx?: number } | null;
    if (historyState && (historyState.idx ?? 0) > 0) {
      navigate(-1);
    } else {
      navigate(ROUTES.DASHBOARD.PAYOUTS);
    }
  };

  return (
    <Box
      p={{ base: "1rem", md: "1rem 3rem" }}
      width="100%"
      height="100%"
      display="flex"
      flexDirection="column"
      gap="1.5rem"
      overflow="auto"
    >
      <Box
        display="flex"
        flexDirection={{ base: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ base: "stretch", md: "center" }}
        bg="white"
        p=".5rem 1rem"
        m={{ md: "-1rem -3rem 0 -3rem", base: "-1rem" }}
        borderBottom="1.5px solid #ebecef80"
        gap={4}
      >
        <Flex alignItems="center" gap={2}>
          <IconButton
            aria-label="Volver"
            size="sm"
            variant="ghost"
            onClick={handleBack}
            color="gray.400"
            _hover={{ bg: "gray.50", color: "gray.800" }}
            borderRadius="full"
          >
            <ArrowLeft size={20} />
          </IconButton>
          <Box>
            <Heading size="xl" fontWeight="semibold">
              Detalle de dispersión
            </Heading>
            <Text
              textStyle="xs"
              color="gray.500"
              fontWeight="light"
              display={{ base: "none", md: "block" }}
            >
              Revisa el estado y los detalles de esta dispersión masiva.
            </Text>
          </Box>
        </Flex>

        {(batch?.status === "pending" || awaitingFunds) && isPayoutApprover && (
          <HStack gap={3} justifyContent={{ base: "stretch", md: "flex-end" }}>
            <Button
              size="sm"
              colorPalette="red"
              variant="outline"
              onClick={handleReject}
              loading={isRejecting}
              flex={{ base: 1, md: "none" }}
            >
              <XCircle size={18} /> Rechazar lote
            </Button>
            {batch?.status === "pending" && (
              <Button
                size="sm"
                onClick={handleApprove}
                loading={isApproving}
                flex={{ base: 1, md: "none" }}
              >
                <CheckCircle2 size={18} /> Aprobar lote
              </Button>
            )}
            {awaitingFunds && shortfall <= 0 && (
              <Button
                size="sm"
                onClick={handleApprove}
                loading={isApproving}
                flex={{ base: 1, md: "none" }}
              >
                <Send size={18} /> Dispersar lote
              </Button>
            )}
          </HStack>
        )}
      </Box>

      <Box display="flex" flexDirection="column" gap="1.5rem">
        {batch && awaitingFunds && (
          <BatchFundingPanel
            batch={batch}
            shortfall={shortfall}
            latest={latest}
            approved={approved}
            canFund={isPayoutApprover}
            onFund={() => setTopUpModalOpen(true)}
          />
        )}
        <BatchInfo batch={batch} isLoading={isBatchesLoading} />
        {batchId && <BatchItemsTable batchId={batchId} />}
      </Box>

      <OtpApprovalModal
        isOpen={otpModalOpen}
        onClose={() => {
          setOtpModalOpen(false);
          setOtpError(null);
        }}
        onConfirm={(otp) => {
          if (batchId) {
            setOtpError(null);
            approveBatch(batchId, otp);
          }
        }}
        hasTotpSecret={user?.hasTotpSecret ?? false}
        isLoading={isApproving}
        error={otpError}
      />
      <TopUpModal
        isOpen={topUpModalOpen}
        onClose={() => setTopUpModalOpen(false)}
        batchId={batchId}
        shortfall={shortfall > 0 ? shortfall : undefined}
      />
    </Box>
  );
}
